import React, { useMemo, useState } from "react";
import { X, MessageCircle, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { buildWhatsAppLink, normalizeZaPhone, waTemplates, waTemplateForStatus } from "../../lib/whatsapp";

/**
 * WhatsAppQuickSendModal — free click-to-chat popup.
 * Generates a magic-link portal URL, drops it into a pre-written template
 * for the current job status, and opens WhatsApp with the message pre-filled.
 * No API charges, no Meta verification.
 *
 * props: job, onClose
 */
export const WhatsAppQuickSendModal = ({ job, onClose }) => {
  const status = (job.status || "scheduled").toLowerCase();
  const defaultKey = waTemplateForStatus(status);
  const [templateKey, setTemplateKey] = useState(defaultKey);
  const [phone, setPhone] = useState(job.contact_mobile || "");
  const [portalUrl, setPortalUrl] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);
  const [message, setMessage] = useState("");
  const [customized, setCustomized] = useState(false);

  const templateArgs = useMemo(() => ({
    client_name: job.client_name,
    job_number: job.job_number,
    fitment_date: job.fitment_date,
    fitment_time: job.fitment_time,
    fitment_address: job.fitment_address,
    tax_invoice_number: job.tax_invoice_number,
    quote_number: job.quotation_number,
    claim_number: job.claim_number,
    vehicle: [job.vehicle_make_model, job.registration].filter(Boolean).join(" · "),
    portal_url: portalUrl,
  }), [job, portalUrl]);

  // Regenerate the message when template / portal_url changes, unless the
  // user has already edited it manually.
  React.useEffect(() => {
    if (customized) return;
    const t = waTemplates[templateKey];
    if (t) setMessage(t(templateArgs));
  }, [templateKey, templateArgs, customized]);

  const generateLink = async () => {
    setGeneratingLink(true);
    try {
      const res = await adminApi.createClientLink(job.id, { scopes: ["view_quote", "accept_quote", "download_docs"] });
      const url = res.portal_url || res.url || "";
      if (url) {
        setPortalUrl(url);
        toast.success("Portal link generated");
      } else toast.error("Could not generate portal link");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate portal link");
    } finally { setGeneratingLink(false); }
  };

  const num = normalizeZaPhone(phone);
  const canSend = !!num && !!message.trim();

  const send = () => {
    if (!canSend) return toast.error("Enter a valid mobile number and a message");
    const url = buildWhatsAppLink(phone, message);
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("WhatsApp opened — tap Send to deliver");
    // Don't close automatically — user might want to send multiple templates
  };

  const copyMessage = async () => {
    try { await navigator.clipboard.writeText(message); toast.success("Message copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="wa-modal">
      <div className="bg-white rounded-md shadow-xl max-w-lg w-full p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#25D366]">
              WhatsApp · free click-to-chat
            </div>
            <h2 className="font-display font-black uppercase text-xl text-[#111619] mt-1">Send via WhatsApp</h2>
            <p className="text-xs text-[#3f4b52] mt-1">
              Opens WhatsApp Business on <b>{phone || "—"}</b> with the message pre-filled. Tap Send from there.
            </p>
          </div>
          <button onClick={onClose} className="text-[#3f4b52]" data-testid="wa-close-btn" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52]">Client mobile</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="081 506 9375"
              className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
              data-testid="wa-phone-input"
            />
            {phone && !num && <div className="text-xs text-[#7f1d1d] mt-1">Doesn&apos;t look like a valid ZA number.</div>}
          </div>

          <div>
            <label className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52]">Template</label>
            <select
              value={templateKey}
              onChange={(e) => { setTemplateKey(e.target.value); setCustomized(false); }}
              className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm bg-white"
              data-testid="wa-template-select"
            >
              <option value="status_scheduled">Status — fitment booked</option>
              <option value="status_en_route">Status — on the way</option>
              <option value="status_on_site">Status — arrived on site</option>
              <option value="status_completed">Status — fitment complete</option>
              <option value="status_invoiced">Invoice ready</option>
              <option value="documents_ready">All documents ready</option>
              <option value="quote_ready">Quote ready for approval</option>
              <option value="insurer_bundle">Insurer bundle (broker/claim)</option>
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52]">Portal magic-link (optional)</label>
              {!portalUrl ? (
                <button
                  type="button"
                  onClick={generateLink}
                  disabled={generatingLink}
                  className="text-xs text-[#f28a16] hover:underline inline-flex items-center gap-1"
                  data-testid="wa-generate-link-btn"
                >
                  {generatingLink ? <Loader2 size={11} className="animate-spin" /> : null}
                  Generate link →
                </button>
              ) : (
                <span className="text-xs text-[#065f46]">✓ Link included</span>
              )}
            </div>
            {portalUrl && (
              <div className="mt-1 text-xs text-[#3f4b52] bg-[#f5f6f6] p-2 rounded-md border border-[#cfd7db] break-all font-mono">
                {portalUrl}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52]">Message preview</label>
              <button
                type="button"
                onClick={copyMessage}
                className="text-xs text-[#3f4b52] hover:text-[#f28a16] inline-flex items-center gap-1"
                data-testid="wa-copy-btn"
              >
                <Copy size={11} /> Copy
              </button>
            </div>
            <textarea
              value={message}
              onChange={(e) => { setMessage(e.target.value); setCustomized(true); }}
              rows={6}
              className="w-full mt-1 px-3 py-2 border border-[#cfd7db] rounded-md text-sm font-mono"
              data-testid="wa-message-textarea"
            />
            <div className="text-xs text-[#3f4b52] mt-1">
              You can edit before sending. Emoji ✅🚗 are fine — WhatsApp renders them natively.
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md hover:bg-[#f5f6f6]"
            data-testid="wa-cancel-btn"
          >Close</button>
          <button
            onClick={send}
            disabled={!canSend}
            data-testid="wa-send-btn"
            className="bg-[#25D366] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
          >
            <MessageCircle size={14} /> Open in WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppQuickSendModal;
